import { ArrowLeft, Trash2, Mail, MessageCircle, Shield } from "lucide-react";
import { Link } from "react-router-dom";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_lead-tracker-96/artifacts/g69u3ad3_D%20Nexus%20Logo.png";

export default function DataDeletionInstructions() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-white border-b shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/login" className="flex items-center gap-2 text-slate-600 hover:text-slate-900">
            <ArrowLeft className="w-4 h-4" />
            Back to Login
          </Link>
          <img src={LOGO_URL} alt="Dnexus Media" className="h-10" />
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
          <div className="flex items-center gap-4 mb-6">
            <div className="p-3 bg-red-100 rounded-xl">
              <Trash2 className="w-8 h-8 text-red-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900">Data Deletion Instructions</h1>
              <p className="text-slate-500">How to request deletion of your data</p>
            </div>
          </div>

          <div className="prose prose-slate max-w-none">
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-8">
              <div className="flex items-start gap-3">
                <Shield className="w-6 h-6 text-blue-600 mt-1" />
                <div>
                  <h3 className="text-lg font-semibold text-blue-800 mt-0 mb-2">Your Privacy Rights</h3>
                  <p className="text-blue-700 mb-0">
                    You have the right to request deletion of your personal data at any time. We are committed to 
                    honoring your data deletion requests in compliance with applicable data protection laws including GDPR.
                  </p>
                </div>
              </div>
            </div>

            <h2 className="text-xl font-semibold text-slate-800 mt-8 mb-4">What Data We Store</h2>
            <p className="text-slate-600 mb-4">When you use Dnexus Media CRM, we may store the following data:</p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li><strong>Account Information:</strong> Name, email, password (encrypted), profile picture</li>
              <li><strong>Business Data:</strong> Leads, contacts, clients, tasks, and notes you create</li>
              <li><strong>Integration Data:</strong> Data from connected services (Facebook, WhatsApp, Email, Google Sheets)</li>
              <li><strong>Activity Logs:</strong> Login history, feature usage, and audit trails</li>
              <li><strong>Attendance Records:</strong> Punch-in/out times, leave requests, salary information</li>
            </ul>

            <h2 className="text-xl font-semibold text-slate-800 mt-8 mb-4">How to Request Data Deletion</h2>
            
            {/* Method 1 */}
            <div className="bg-slate-50 rounded-xl p-6 mb-6">
              <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2 mt-0 mb-3">
                <span className="w-8 h-8 bg-indigo-600 text-white rounded-full flex items-center justify-center text-sm font-bold">1</span>
                Through Your Account (Recommended)
              </h3>
              <ol className="list-decimal pl-6 text-slate-600 space-y-2">
                <li>Log in to your Dnexus Media CRM account</li>
                <li>Go to <strong>Settings</strong> → <strong>Profile</strong></li>
                <li>Scroll down to the <strong>"Delete Account"</strong> section</li>
                <li>Click <strong>"Request Account Deletion"</strong></li>
                <li>Confirm your request by entering your password</li>
                <li>Your data will be deleted within 30 days</li>
              </ol>
            </div>

            {/* Method 2 */}
            <div className="bg-slate-50 rounded-xl p-6 mb-6">
              <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2 mt-0 mb-3">
                <span className="w-8 h-8 bg-indigo-600 text-white rounded-full flex items-center justify-center text-sm font-bold">2</span>
                Via Email Request
              </h3>
              <p className="text-slate-600 mb-4">
                Send an email to our privacy team with your deletion request:
              </p>
              <div className="bg-white border rounded-lg p-4 mb-4">
                <p className="text-slate-700 mb-2"><strong>To:</strong> privacy@dnexusmedia.com</p>
                <p className="text-slate-700 mb-2"><strong>Subject:</strong> Data Deletion Request</p>
                <p className="text-slate-700 mb-0"><strong>Include:</strong></p>
                <ul className="list-disc pl-6 text-slate-600 mt-2">
                  <li>Your registered email address</li>
                  <li>Your full name</li>
                  <li>Business name (if applicable)</li>
                  <li>Reason for deletion (optional)</li>
                </ul>
              </div>
              <p className="text-slate-500 text-sm">
                We will verify your identity and process your request within 30 days.
              </p>
            </div>

            {/* Method 3 - Facebook Users */}
            <div className="bg-slate-50 rounded-xl p-6 mb-6">
              <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2 mt-0 mb-3">
                <span className="w-8 h-8 bg-indigo-600 text-white rounded-full flex items-center justify-center text-sm font-bold">3</span>
                For Facebook Integration Users
              </h3>
              <p className="text-slate-600 mb-4">
                If you connected your Facebook account to Dnexus Media CRM:
              </p>
              <ol className="list-decimal pl-6 text-slate-600 space-y-2">
                <li>Go to your <a href="https://www.facebook.com/settings?tab=applications" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">Facebook Settings → Apps and Websites</a></li>
                <li>Find "Dnexus Media CRM" in the list</li>
                <li>Click <strong>"Remove"</strong> to revoke access</li>
                <li>Additionally, contact us to delete any data we've already stored</li>
              </ol>
            </div>

            <h2 className="text-xl font-semibold text-slate-800 mt-8 mb-4">What Happens After Deletion</h2>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Your account will be deactivated immediately</li>
              <li>All personal data will be permanently deleted within 30 days</li>
              <li>Some anonymized data may be retained for analytics purposes</li>
              <li>Data required for legal compliance may be retained as required by law</li>
              <li>Backup copies will be purged within 90 days</li>
            </ul>

            <h2 className="text-xl font-semibold text-slate-800 mt-8 mb-4">Data We Cannot Delete</h2>
            <p className="text-slate-600 mb-4">
              In some cases, we may be unable to delete certain data:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Data required for legal or regulatory compliance</li>
              <li>Data necessary for resolving disputes or enforcing agreements</li>
              <li>Anonymized or aggregated data that cannot identify you</li>
            </ul>

            <h2 className="text-xl font-semibold text-slate-800 mt-8 mb-4">Contact Us</h2>
            <p className="text-slate-600 mb-4">
              If you have any questions about data deletion or need assistance:
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-slate-50 p-4 rounded-lg">
                <div className="flex items-center gap-3 mb-2">
                  <Mail className="w-5 h-5 text-indigo-600" />
                  <strong className="text-slate-700">Email</strong>
                </div>
                <p className="text-slate-600">privacy@dnexusmedia.com</p>
              </div>
              <div className="bg-slate-50 p-4 rounded-lg">
                <div className="flex items-center gap-3 mb-2">
                  <MessageCircle className="w-5 h-5 text-green-600" />
                  <strong className="text-slate-700">Response Time</strong>
                </div>
                <p className="text-slate-600">Within 5 business days</p>
              </div>
            </div>

            <div className="mt-8 p-6 bg-amber-50 border border-amber-200 rounded-xl">
              <h3 className="text-lg font-semibold text-amber-800 mt-0 mb-2">Important Note</h3>
              <p className="text-amber-700 mb-0">
                Data deletion is permanent and cannot be undone. Please make sure to export any data you need before 
                requesting deletion. If you're a business admin, deleting your account may affect other team members' 
                access to shared data.
              </p>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t mt-12">
        <div className="max-w-4xl mx-auto px-4 py-6 text-center text-slate-500 text-sm">
          © {new Date().getFullYear()} Dnexus Media. All rights reserved.
          <div className="mt-2 space-x-4">
            <Link to="/privacy-policy" className="text-indigo-600 hover:underline">Privacy Policy</Link>
            <Link to="/terms-of-service" className="text-indigo-600 hover:underline">Terms of Service</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
